import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';
import { formatarPreco } from '../utils/priceUtils';
import { formatDate } from '../utils/dateUtils';

const CommissionHistory = ({ salonId, professionals }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfessional, setSelectedProfessional] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('current'); // 'current', 'last', 'custom'
  const [showDetails, setShowDetails] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [recentPayments, setRecentPayments] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Carregar histórico de pagamentos
  useEffect(() => {
    const fetchPaymentHistory = async () => {
      if (!salonId) return;
      
      try {
        setLoading(true);
        
        // Determinar datas para filtragem
        const today = new Date();
        let startDate, endDate;
        
        if (selectedPeriod === 'current') {
          // Mês atual
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        } else if (selectedPeriod === 'last') {
          // Mês anterior
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        } else if (selectedPeriod === 'custom' && customStartDate && customEndDate) {
          // Período personalizado
          startDate = new Date(`${customStartDate}T00:00:00`);
          endDate = new Date(`${customEndDate}T23:59:59`);
        } else {
          // Padrão: últimos 30 dias
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 30);
          endDate = today;
        }
        
        // Buscar pagamentos
        const paymentsRef = collection(db, `salons/${salonId}/commissionPayments`);

        console.log('Buscando pagamentos de comissões...');

        // Simplificando a consulta para usar apenas uma condição
        let paymentsQuery;
        if (selectedProfessional === 'all') {
          // Usando apenas a data para filtrar
          paymentsQuery = query(
            paymentsRef,
            where('paymentDate', '>=', startDate),
            where('paymentDate', '<=', endDate)
            // Removendo orderBy para evitar necessidade de índice composto
          );
        } else {
          // Usando apenas o profissional para filtrar (sem data nem ordenação)
          paymentsQuery = query(
            paymentsRef,
            where('professionalId', '==', selectedProfessional)
            // Removendo filtros de data e orderBy para evitar necessidade de índice composto
          );
        }
        
        const snapshot = await getDocs(paymentsQuery);
        console.log(`Total de pagamentos encontrados: ${snapshot.docs.length}`);
        
        // Converter e aplicar filtragem manual se necessário
        let paymentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Se filtramos por profissional, precisamos filtrar manualmente por data
        if (selectedProfessional !== 'all') {
          paymentsData = paymentsData.filter(payment => {
            const paymentDate = payment.paymentDate instanceof Date 
              ? payment.paymentDate 
              : payment.paymentDate.toDate();
            
            return paymentDate >= startDate && paymentDate <= endDate;
          });
        }
        
        // Ordenar manualmente por data mais recente
        paymentsData.sort((a, b) => {
          const dateA = a.paymentDate instanceof Date ? a.paymentDate : a.paymentDate.toDate();
          const dateB = b.paymentDate instanceof Date ? b.paymentDate : b.paymentDate.toDate();
          return dateB - dateA; // Ordem decrescente (mais recente primeiro)
        });
        
        console.log(`Pagamentos após filtragem manual: ${paymentsData.length}`);
        setPayments(paymentsData);
        
        // Simplificar a consulta de pagamentos recentes também
        const recentQuery = query(
          paymentsRef,
          selectedProfessional !== 'all' 
            ? where('professionalId', '==', selectedProfessional)
            : where('paymentDate', '!=', null) // Condição simples para buscar todos
        );
        
        const recentSnapshot = await getDocs(recentQuery);
        let recentData = recentSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Ordenar manualmente e pegar os 5 mais recentes
        recentData.sort((a, b) => {
          const dateA = a.paymentDate instanceof Date ? a.paymentDate : a.paymentDate.toDate();
          const dateB = b.paymentDate instanceof Date ? b.paymentDate : b.paymentDate.toDate();
          return dateB - dateA;
        });
        
        recentData = recentData.slice(0, 5);
        console.log(`Pagamentos recentes encontrados: ${recentData.length}`);
        setRecentPayments(recentData);
        
        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar histórico de pagamentos:', error);
        setLoading(false);
      }
    };
    
    fetchPaymentHistory();
  }, [salonId, selectedProfessional, selectedPeriod, customStartDate, customEndDate]);

  // Calcular totais
  const totalPaid = payments.reduce((sum, payment) => sum + (payment.totalValue || 0), 0);
  const totalServices = payments.reduce((sum, payment) => sum + (payment.services || 0), 0);

  // Função para aplicar filtros personalizados
  const applyCustomFilter = () => {
    setSelectedPeriod('custom');
    setFilterModalOpen(false);
  };

  // Renderizar período selecionado em texto legível
  const renderPeriodText = () => {
    const today = new Date();
    
    if (selectedPeriod === 'current') {
      const monthName = today.toLocaleString('pt-BR', { month: 'long' });
      return `${monthName} de ${today.getFullYear()}`;
    } else if (selectedPeriod === 'last') {
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const monthName = lastMonth.toLocaleString('pt-BR', { month: 'long' });
      return `${monthName} de ${lastMonth.getFullYear()}`;
    } else if (selectedPeriod === 'custom' && customStartDate && customEndDate) {
      return `${formatDate(customStartDate)} a ${formatDate(customEndDate)}`;
    }
    
    return 'Período personalizado';
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {/* Seção de cabeçalho com filtros rápidos */}
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex flex-wrap items-center justify-between">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Histórico de Pagamentos de Comissões
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {renderPeriodText()}
            {selectedProfessional !== 'all' && (
              <span className="ml-1">
                • {professionals.find(p => p.id === selectedProfessional)?.nome || 'Profissional'}
              </span>
            )}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center mt-3 sm:mt-0">
          {/* Seletor de Período */}
          <div className="mr-3 flex">
            <button 
              onClick={() => setSelectedPeriod('current')}
              className={`px-3 py-1.5 text-xs rounded-l-md ${
                selectedPeriod === 'current' 
                  ? 'bg-blue-100 text-blue-700 font-medium' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Mês Atual
            </button>
            <button 
              onClick={() => setSelectedPeriod('last')}
              className={`px-3 py-1.5 text-xs ${
                selectedPeriod === 'last' 
                  ? 'bg-blue-100 text-blue-700 font-medium' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Mês Anterior
            </button>
            <button 
              onClick={() => setFilterModalOpen(true)}
              className={`px-3 py-1.5 text-xs rounded-r-md ${
                selectedPeriod === 'custom' 
                  ? 'bg-blue-100 text-blue-700 font-medium' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              } flex items-center`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
              </svg>
              Filtros
            </button>
          </div>
          
          {/* Seletor de Profissional */}
          <select
            value={selectedProfessional}
            onChange={(e) => setSelectedProfessional(e.target.value)}
            className="border border-gray-300 rounded-md py-1.5 pl-3 pr-7 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todos os Profissionais</option>
            {professionals.map(professional => (
              <option key={professional.id} value={professional.id}>{professional.nome}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Resumo do período */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-md p-3">
            <p className="text-sm text-blue-800 font-medium">Total Pago</p>
            <p className="text-xl font-bold text-blue-900">{formatarPreco(totalPaid)}</p>
          </div>
          
          <div className="bg-green-50 rounded-md p-3">
            <p className="text-sm text-green-800 font-medium">Serviços Comissionados</p>
            <p className="text-xl font-bold text-green-900">{totalServices}</p>
          </div>
        </div>
      </div>
      
      {/* Conteúdo Principal */}
      <div className="px-4 py-5 sm:p-6">
        {loading ? (
          <div className="animate-pulse">
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-20 bg-gray-200 rounded mb-4"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        ) : (
          <>
            {/* Pagamentos Recentes (sempre mostrados) */}
            {recentPayments.length > 0 && selectedPeriod === 'current' && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Pagamentos Recentes</h4>
                <div className="space-y-3">
                  {recentPayments.map(payment => (
                    <div key={payment.id} className="bg-white border rounded-lg shadow-sm p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">{payment.professionalName}</p>
                          <p className="text-sm text-gray-500">
                            Pago em {formatDate(payment.paymentDate)}
                            {payment.periodStart && payment.periodEnd && (
                              <span> • Período: {formatDate(payment.periodStart)} - {formatDate(payment.periodEnd)}</span>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-gray-900">{formatarPreco(payment.totalValue)}</p>
                          <p className="text-xs text-gray-500">{payment.services} serviços</p>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-end">
                        <button 
                          onClick={() => setShowDetails(showDetails === payment.id ? null : payment.id)}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          {showDetails === payment.id ? 'Ocultar detalhes' : 'Ver detalhes'}
                          <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ml-1 transition-transform ${showDetails === payment.id ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                      
                      {/* Detalhes do pagamento */}
                      {showDetails === payment.id && payment.details && (
                        <div className="mt-3 border-t pt-3">
                          <div className="rounded-md bg-gray-50 overflow-hidden">
                            <div className="max-h-64 overflow-y-auto">
                              <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Cliente
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Data
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Tipo
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Serviço/Produto
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Valor
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Comissão
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {payment.details.map((detail, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 whitespace-nowrap text-sm">{detail.client}</td>
                                      <td className="px-3 py-2 whitespace-nowrap text-sm">{formatDate(detail.date)}</td>
                                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                                        {detail.type === 'product' ? 'Produto' : 'Serviço'}
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                                        {detail.type === 'product' ? detail.productName : detail.serviceName}
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right">
                                        {formatarPreco(detail.type === 'product' ? detail.productValue : detail.serviceValue)}
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium">
                                        {formatarPreco(detail.commissionValue)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr className="bg-gray-50">
                                    <td colSpan="5" className="px-3 py-2 text-right font-medium text-gray-700">Total</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-right font-bold text-gray-900">
                                      {formatarPreco(payment.totalValue)}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Pagamentos do período filtrado */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {selectedPeriod === 'current' ? 'Todos os Pagamentos do Mês' : 'Pagamentos do Período'}
              </h4>
              
              {payments.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p className="text-gray-600">Nenhum pagamento encontrado para o período selecionado</p>
                  <p className="text-sm text-gray-500 mt-1">Tente ajustar os filtros ou selecionar outro período</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Profissional
                        </th>
                        <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data
                        </th>
                        <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Período
                        </th>
                        <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Serviços
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payments.map(payment => (
                        <tr key={payment.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{payment.professionalName}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm text-gray-500">{formatDate(payment.paymentDate)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm text-gray-500">
                              {payment.periodStart && payment.periodEnd ? (
                                `${formatDate(payment.periodStart)} - ${formatDate(payment.periodEnd)}`
                              ) : (
                                'Não especificado'
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm text-gray-500">{payment.services || 0}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="text-sm font-medium text-gray-900">{formatarPreco(payment.totalValue)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button 
                              onClick={() => setShowDetails(showDetails === payment.id ? null : payment.id)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              {showDetails === payment.id ? 'Ocultar' : 'Detalhes'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Modal de Filtro */}
      {filterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Filtrar Pagamentos</h3>
              <button 
                onClick={() => setFilterModalOpen(false)} 
                className="text-gray-400 hover:text-gray-500"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="px-4 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Inicial
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Final
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profissional
                  </label>
                  <select
                    value={selectedProfessional}
                    onChange={(e) => setSelectedProfessional(e.target.value)}
                    className="w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">Todos os Profissionais</option>
                    {professionals.map(professional => (
                      <option key={professional.id} value={professional.id}>{professional.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="px-4 py-3 bg-gray-50 text-right rounded-b-lg">
              <button
                onClick={() => setFilterModalOpen(false)}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 mr-3"
              >
                Cancelar
              </button>
              <button
                onClick={applyCustomFilter}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                disabled={!customStartDate || !customEndDate}
              >
                Aplicar Filtro
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionHistory; 